import { Stack } from '@mui/material';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import {
  DynamicIcon,
  FormAutocompleteProps,
  FormLabel,
  FormTextField,
  FormTextFieldProps,
} from 'src/components';
import {
  TRIGGER_FONT_SIZE,
  TRIGGER_ICON_SIZE_PX,
  TRIGGER_LINE_HEIGHT,
} from 'src/components/SelectTrigger';
import { SIDE_PANEL_ROW_CONTENT_HEIGHT, SidePanelRow } from 'src/components/SidePanelRow';
import { ProjectDateTriggerField } from 'src/components/SidePanelTabs/Projects/project-date-trigger-field';
import { ProjectLostReasonSelectMenuField } from 'src/components/SidePanelTabs/Projects/project-lost-reason-select-menu-field';
import { ProjectStageSelectMenuField } from 'src/components/SidePanelTabs/Projects/project-stage-select-menu-field';
import { SiteAddress } from 'src/components/SiteComponents/site-address';
import {
  Address,
  Project,
  ProjectStages,
  useGroupRules,
  useProjectStages,
  UserGroupRules,
} from 'src/graphql';
import { useProjectLostReasons } from 'src/graphql/entities/project-lost-reason';
import { useAssociatedSiteForm } from 'src/pages/Conversations/SidePanel/ConversationDetails/ConversationSite/use-associated-site-form';
import { useOpenSite } from 'src/utils/hooks';

export function useProjectForm(
  project?: Project,
  address?: Partial<Address>,
  disabled = false,
) {
  const { groupRules } = useGroupRules();

  const enabledChangeProjectStatus = Boolean(
    groupRules[UserGroupRules.AccessToChangeProjectStatus],
  );

  const { associatedSiteFormFields, updateAddress, commonProps } = useAssociatedSiteForm(
    address,
    disabled,
    { project },
  );

  const openSite = useOpenSite();

  const { projectStages, loading: loadingProjectStages } = useProjectStages();
  const { projectLostReasons } = useProjectLostReasons();

  const currentProjectStage = useMemo(
    () => projectStages.find((item) => item.id === project?.projectStageId) || null,
    [project?.projectStageId, projectStages],
  );

  const currentProjectLostReason = useMemo(() => {
    if (!project) {
      return null;
    }
    return projectLostReasons.find((item) => item.id === project.projectLostReasonId) || null;
  }, [project, projectLostReasons]);

  const shouldShowDay1Date = project && project.projectStageId === ProjectStages.ServiceClient;
  const shouldShowWarrantyExpiration =
    project && project.projectStageId === ProjectStages.ServiceClient;
  const shouldShowEstimatedSigning =
    project && project.projectStageId === ProjectStages.Proposal;
  const shouldShowEstimatedDay1 =
    project &&
    (project.projectStageId === ProjectStages.Proposal ||
      project.projectStageId === ProjectStages.ActiveProject);
  const shouldShowLostReason =
    project && project.projectStageId === ProjectStages.ProposalLost;

  const projectFormFields = useMemo(
    () =>
      [
        {
          type: 'textfield',
          name: 'site' as const,
          label: 'Site',
          renderField: () => (
            <SidePanelRow key="site">
              <FormLabel label="Site" />
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                minWidth={0}
                width="100%"
                paddingLeft={1}
                height={SIDE_PANEL_ROW_CONTENT_HEIGHT}
              >
                <SiteAddress
                  address={address as Address}
                  onClick={(event) => openSite(event, address as Address)}
                  tooltipText="Click to open site"
                  icon={
                    <DynamicIcon
                      name="home"
                      library="lucide"
                      size={TRIGGER_ICON_SIZE_PX}
                      sx={{ color: (theme) => theme.palette.custom.OV.sys.onSurfaceVariant }}
                    />
                  }
                  textSx={{
                    fontSize: TRIGGER_FONT_SIZE,
                    fontWeight: (theme) => theme.typography.fontWeightRegular,
                  }}
                />
              </Stack>
            </SidePanelRow>
          ),
        },
        {
          type: 'textfield',
          name: 'projectName' as const,
          defaultValue: project?.name || '',
          label: 'Project Name',
          renderField,
        },
        {
          type: 'autocomplete' as const,
          name: 'projectStage',
          label: 'Stage',
          required: 'Stage is required',
          defaultValue: currentProjectStage
            ? { id: String(currentProjectStage.id), label: currentProjectStage.label }
            : null,
          options: projectStages.map((stage) => ({
            id: String(stage.id),
            label: stage.name,
          })),
          ...commonProps,
          renderField: (field: FormAutocompleteProps, formDisabled?: boolean) => (
            <ProjectStageSelectMenuField field={field} disabled={formDisabled} />
          ),
          disabled: commonProps.disabled || !enabledChangeProjectStatus,
          loading: loadingProjectStages,
        },
        ...associatedSiteFormFields,
        {
          type: 'textfield',
          name: 'day1Date' as const,
          defaultValue: project?.day1StartDate ? dayjs(project.day1StartDate) : null,
          label: 'Day1 Date',
          renderField: (field: FormTextFieldProps, formDisabled?: boolean) => (
            <ProjectDateTriggerField field={field} disabled={formDisabled} />
          ),
          hidden: !shouldShowDay1Date,
        },
        {
          type: 'textfield',
          name: 'warrantyExpiration' as const,
          defaultValue: project?.warrantyExpirationDate
            ? dayjs(project.warrantyExpirationDate)
            : null,
          label: 'Warranty Exp.',
          renderField: (field: FormTextFieldProps, formDisabled?: boolean) => (
            <ProjectDateTriggerField field={field} disabled={formDisabled} />
          ),
          hidden: !shouldShowWarrantyExpiration,
        },
        {
          type: 'textfield',
          name: 'estimatedSigning' as const,
          defaultValue: project?.signingDate ? dayjs(project.signingDate) : null,
          label: 'Est. Signing',
          renderField: (field: FormTextFieldProps, formDisabled?: boolean) => (
            <ProjectDateTriggerField field={field} disabled={formDisabled} />
          ),
          hidden: !shouldShowEstimatedSigning,
        },
        {
          type: 'textfield',
          name: 'estimatedDay1' as const,
          defaultValue: project?.estimatedDay1Date ? dayjs(project.estimatedDay1Date) : null,
          label: 'Est. Day1',
          renderField: (field: FormTextFieldProps, formDisabled?: boolean) => (
            <ProjectDateTriggerField field={field} disabled={formDisabled} />
          ),
          hidden: !shouldShowEstimatedDay1,
        },
        {
          type: 'autocomplete',
          name: 'projectLostReason' as const,
          label: 'Lost Reason',
          options: projectLostReasons.map((stage) => ({ id: stage.id, label: stage.name })),
          defaultValue: currentProjectLostReason,
          ...commonProps,
          renderField: (field: FormAutocompleteProps, formDisabled?: boolean) => (
            <ProjectLostReasonSelectMenuField field={field} disabled={formDisabled} />
          ),
          hidden: !shouldShowLostReason,
        },
      ] as FormAutocompleteProps[],
    [
      address,
      associatedSiteFormFields,
      commonProps,
      currentProjectLostReason,
      currentProjectStage,
      enabledChangeProjectStatus,
      loadingProjectStages,
      openSite,
      project?.name,
      project?.day1StartDate,
      project?.estimatedDay1Date,
      project?.signingDate,
      project?.warrantyExpirationDate,
      projectLostReasons,
      projectStages,
      shouldShowDay1Date,
      shouldShowEstimatedDay1,
      shouldShowEstimatedSigning,
      shouldShowLostReason,
      shouldShowWarrantyExpiration,
    ],
  );

  return { projectFormFields, updateAddress };
}

function renderField(field: FormAutocompleteProps, disabled?: boolean) {
  const { defaultValue, label } = field;
  return (
    <SidePanelRow key={label}>
      <FormLabel label={label} />
      <Stack direction="row" width="100%" gap={1}>
        <FormTextField
          type="textfield"
          name={field.name}
          label=""
          fullWidth
          disabled={disabled}
          defaultValue={(defaultValue as string) || undefined}
          textFieldProps={{
            size: 'small',
            variant: 'standard',
            // mirrors the inline SelectMenu trigger, so renaming a project offers the
            // same hit area, hover surface and text size as every other editable row
            sx: {
              boxSizing: 'border-box',
              height: SIDE_PANEL_ROW_CONTENT_HEIGHT,
              justifyContent: 'center',
              borderRadius: '0.5rem',
              padding: (theme) => theme.spacing(0.5, 1),
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: (theme) => theme.palette.custom.OV.sys.surface,
              },
              '& .MuiInput-root, & .MuiInput-input': {
                cursor: 'pointer',
                fontSize: TRIGGER_FONT_SIZE,
                lineHeight: TRIGGER_LINE_HEIGHT,
              },
              '&.Mui-focused': {
                backgroundColor: (theme) => theme.palette.custom.OV.sys.surfaceContainerHigh,
              },
            },
          }}
        />
      </Stack>
    </SidePanelRow>
  );
}
